import React, { useState, useEffect, useCallback, useId, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { organizationAPI } from '../services/api';
import { Layers, Package, Pencil, Plus, RefreshCw, Tag, Trash2, X } from 'lucide-react';
import OrgTemplatesManager from './OrgTemplatesManager';
import OrgPriceListsManager from './OrgPriceListsManager';
import OrgCatalogConsistency from './OrgCatalogConsistency';
import {
  addBaseSize, buildOrgProductPayload, feeSelectLocked, isFeeForm, namedSizes, sizeRemovalLocked, sizeRequired,
  OrgPayloadOptions, OrgSizeFormRow
} from '../utils/orgProductPayload';
import CatalogColorPicker, { CatalogToneDot } from './shared/CatalogColorPicker';
import { autoCategoryTone } from '../utils/catalogHelpers';
import ConfirmModal from './shared/ConfirmModal';
import { LoadFailed } from './settings/instantSettings';
import { SettingsSwitch } from './settings/storeSettingsForm';
import { SheetField, SheetRow } from './settings/sheet';
import {
  Column, DANGER_GHOST_BTN, DataTable, DetailPanel, Disclosure, EmptyState, FilterSelect, GHOST_BTN, ICON_BTN, INPUT_CLS,
  ListPage, ListToolbar, PRIMARY_BTN, SELECT_CLS, StatusChip, matchesSearch, useListLayout,
} from './shared/listPage';
import { Banner, COMPACT_INPUT, FlashMessage, FlashToast, GroupTitle, LINK_BTN, SubTabs, chipClass } from './orgPages/ui';

/**
 * «Κεντρικός κατάλογος» του οργανισμού (π.χ. Κουλουράδες, 27 καταστήματα): εδώ ζουν τα κεντρικά
 * προϊόντα/κατηγορίες· κάθε αλλαγή πηγαίνει στα καταστήματα ως αίτημα συγχρονισμού.
 *
 * Νέα εμφάνιση (14/10/2026): ίδιο σχήμα με τον «Κατάλογο» του καταστήματος — δέντρο κατηγοριών
 * αριστερά (φίλτρο), πίνακας προϊόντων, και συρτάρι δεξιά για προϊόν/κατηγορία/μαζική αλλαγή — μέσα
 * στη σελίδα «Πίνακας» (shared/listPage) με τις υπο-καρτέλες του καταλόγου. ΙΔΙΑ endpoints, ίδιο σώμα
 * (utils/orgProductPayload), ίδιοι έλεγχοι· οι διαγραφές ρωτούν με το ConfirmModal αντί για το
 * παράθυρο του browser. Το συρτάρι είναι παράθυρο (aria-modal): ως 14/10 το πλαϊνό ήταν «ανοιχτό» και
 * ένα κλικ σε άλλο προϊόν άλλαζε τη φόρμα χωρίς να ρωτήσει — με αποθηκευμένες αλλαγές χαμένες.
 */

// ============================================================
// Types
// ============================================================

interface OrgCategory {
  id: number;
  organization_id: number;
  parent_id: number | null;
  name: string;
  description: string | null;
  image_url: string | null;
  display_order: number;
  is_active: boolean;
  product_count: number;
  parent_name: string | null;
  // Store availability: null/empty = all stores; an array = only those store ids.
  store_ids: number[] | null;
  // Τόνος της παλέτας για το κουτί στο ταμείο (migration 264)· null = αυτόματο. Συγχρονίζεται όπως το name.
  color?: string | null;
}

interface OrgSize {
  id: number;
  org_product_id: number;
  name: string;
  price_modifier: number;
  is_default: boolean;
  display_order: number;
  is_available: boolean;
  price_modifier_take_away: number | null;
  price_modifier_delivery: number | null;
  price_modifier_dine_in: number | null;
}

interface OrgIngredient {
  id: number;
  org_product_id: number;
  ingredient_name: string;
  extra_price: number;
  is_default: boolean;
  display_order: number;
  category: string | null;
}

interface OrgOptionChoice {
  name: string;
  price_modifier: number;
  is_default: boolean;
  display_order: number;
}

interface OrgOptionGroup {
  id: number;
  name: string;
  selection_type: 'single' | 'multiple';
  is_required: boolean;
  display_order: number;
  choices: OrgOptionChoice[];
}

interface OrgProduct {
  id: number;
  organization_id: number;
  category_id: number;
  category_name: string;
  name: string;
  description: string | null;
  image_url: string | null;
  vat: number;
  product_type: string;
  is_available: boolean;
  display_order: number;
  vat_take_away: number | null;
  vat_delivery: number | null;
  vat_dine_in: number | null;
  fee_category: number | null;
  auto_plastic_fee_count: number | null;
  is_weight_variable: boolean | null;
  is_open_price: boolean | null;
  barcode: string | null;
  sell_unit: string | null;
  max_discount_percent: number | null;
  xenios_product_id: string | null;
  store_ids: number[] | null; // null = available in all stores; array = only those stores
  color?: string | null; // τόνος της κάρτας στο ταμείο (migration 264)· null = χωρίς
  sizes: OrgSize[];
  simple_ingredients: OrgIngredient[];
  option_groups: OrgOptionGroup[];
}

interface SyncRequest {
  id: number;
  store_id: number;
  store_name: string;
  change_type: string;
  change_summary: string;
  change_data: any;
  status: string;
  created_by_name: string;
  created_at: string;
  reviewed_at: string | null;
}

interface OrgStoreItem {
  id: number;
  name: string;
}

interface Props {
  orgId: number;
  orgName: string;
  canManage: boolean;
}

type SubTabId = 'categories' | 'products' | 'sync' | 'templates' | 'pricelists' | 'consistency';
type LoadState = 'loading' | 'ready' | 'error' | 'forbidden';

// ============================================================
// Μικρά κομμάτια της σελίδας
// ============================================================

/**
 * «Διαθέσιμο σε καταστήματα» (προϊόν και κατηγορία, ίδια λογική): null = όλα τα καταστήματα (και όσα
 * προστεθούν), πίνακας = μόνο αυτά. Το «Συγκεκριμένα» ξεκινά με ΟΛΑ επιλεγμένα — όπως πριν.
 */
const StoreScope: React.FC<{
  stores: OrgStoreItem[];
  value: number[] | null;
  onChange: (next: number[] | null) => void;
  someHint: string;
}> = ({ stores, value, onChange, someHint }) => {
  const { t } = useTranslation(['orgPages']);
  return (
    <SheetField label={t('orgPages:catalog.form.stores')} hint={value === null ? t('orgPages:catalog.form.storesAllHint') : someHint}>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('orgPages:catalog.form.stores')}>
        <button type="button" role="radio" aria-checked={value === null} className={chipClass(value === null)} onClick={() => onChange(null)}>
          {t('orgPages:catalog.form.storesAll')}
        </button>
        <button type="button" role="radio" aria-checked={value !== null} className={chipClass(value !== null)}
          onClick={() => onChange(value === null ? stores.map(s => s.id) : value)}>
          {t('orgPages:catalog.form.storesSome')}
        </button>
      </div>
      {value !== null && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {stores.map(s => {
            const sel = value.includes(s.id);
            return (
              <button key={s.id} type="button" aria-pressed={sel} className={chipClass(sel)}
                onClick={() => onChange(sel ? value.filter(id => id !== s.id) : [...value, s.id])}>
                {(s as any).display_name || s.name}
                <span className="text-xs font-normal text-t4 tabular-nums">#{s.id}</span>
              </button>
            );
          })}
        </div>
      )}
    </SheetField>
  );
};

/** Μικρό «Από πρότυπο…»: διαλέγεις πρότυπο και γεμίζει η λίστα — ίδια συμπεριφορά με πριν. */
const TemplateSelect: React.FC<{ templates: any[]; label: string; onPick: (tmpl: any) => void }> = ({ templates, label, onPick }) => {
  const { t } = useTranslation(['admin']);
  if (templates.length === 0) return null;
  return (
    <select
      aria-label={label}
      title={label}
      defaultValue=""
      onChange={e => {
        const tmpl = templates.find((x: any) => x.id === parseInt(e.target.value));
        if (tmpl) onPick(tmpl);
        e.target.value = '';
      }}
      className={`${COMPACT_INPUT} max-w-[12rem] pr-7`}
    >
      <option value="" disabled>{t('admin:fromTemplate')}</option>
      {templates.map((tmpl: any) => <option key={tmpl.id} value={tmpl.id}>{tmpl.name}</option>)}
    </select>
  );
};

// ============================================================
// Component
// ============================================================

const OrgCatalogManager: React.FC<Props> = ({ orgId, orgName, canManage }) => {
  const { t } = useTranslation(['admin', 'common', 'orgPages']);
  const uid = useId();
  const showXeniosField = orgName?.toLowerCase().includes('bread factory');
  // State
  const [subTab, setSubTab] = useState<SubTabId>('products');
  const [categories, setCategories] = useState<OrgCategory[]>([]);
  const [products, setProducts] = useState<OrgProduct[]>([]);
  const [syncRequests, setSyncRequests] = useState<SyncRequest[]>([]);
  const [syncFilter, setSyncFilter] = useState<'pending' | 'all'>('pending');
  const [orgStores, setOrgStores] = useState<OrgStoreItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [search, setSearch] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<number | null>(null);
  const [toast, setToast] = useState<FlashMessage | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Import from store
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStoreId, setImportStoreId] = useState<string>('');
  const [importing, setImporting] = useState(false);

  // Batch operations
  const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(new Set());
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchUpdates, setBatchUpdates] = useState<{ field: string; value: string }>({ field: 'vat', value: '' });
  const [batchProcessing, setBatchProcessing] = useState(false);

  // Selective sync (target stores picker)
  const [selectedTargetStores, setSelectedTargetStores] = useState<number[]>([]);

  // Category modal
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<OrgCategory | null>(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', parent_id: '' as string, display_order: 0, is_active: true, store_ids: null as number[] | null, color: null as string | null });
  const [saving, setSaving] = useState(false);

  // Product modal
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<OrgProduct | null>(null);
  const emptyProductForm = {
    // Χωρίς τιμή προϊόντος: η τιμή ζει στα μεγέθη (τιμοκατάλογοι), και ό,τι φορτωνόταν εδώ
    // ξαναγραφόταν μπαγιάτικο στην αποθήκευση (FE-9).
    category_id: '' as string, name: '', description: '', vat: '24',
    product_type: 'ΠΡΟΙΟΝ', is_available: true, display_order: 0,
    vat_take_away: '', vat_delivery: '', vat_dine_in: '',
    fee_category: '' as string, auto_plastic_fee_count: '0',
    is_weight_variable: false, is_open_price: false,
    barcode: '', sell_unit: '', max_discount_percent: '',
    xenios_product_id: '',
    // Store availability: null = available in ALL stores; an array = only those store ids.
    store_ids: null as number[] | null,
    // Χρώμα της κάρτας στο ταμείο· null = «Χωρίς».
    color: null as string | null,
    sizes: [] as OrgSizeFormRow[],
    simple_ingredients: [] as { ingredient_name: string; extra_price: string; is_default: boolean; category: string }[],
    option_groups: [] as { name: string; selection_type: 'single' | 'multiple'; is_required: boolean; template_id?: number | null; choices: { name: string; price_modifier: string; is_default: boolean }[] }[]
  };
  const [productForm, setProductForm] = useState({ ...emptyProductForm });
  const [channelVatOpen, setChannelVatOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Επιβεβαιώσεις διαγραφής (ConfirmModal αντί για window.confirm)
  const [deleteProductTarget, setDeleteProductTarget] = useState<OrgProduct | null>(null);
  const [deleteCategoryTarget, setDeleteCategoryTarget] = useState<OrgCategory | null>(null);

  // Templates for product modal
  const [sizeTemplates, setSizeTemplates] = useState<any[]>([]);
  const [ingredientTemplates, setIngredientTemplates] = useState<any[]>([]);
  const [optionTemplates, setOptionTemplates] = useState<any[]>([]);

  // ============================================================
  // Toast helper
  // ============================================================

  const showToast = (type: 'success' | 'error', message: string) => {
    clearTimeout(toastTimer.current);
    setToast({ type, message });
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  };
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // ============================================================
  // Data loading
  // ============================================================

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [catData, prodData, storesData, sizeTmpl, ingTmpl, optTmpl] = await Promise.all([
        organizationAPI.getCatalogCategories(orgId),
        organizationAPI.getCatalogProducts(orgId),
        organizationAPI.getOrgStores(orgId),
        organizationAPI.getSizeTemplates(orgId).catch(() => ({ templates: [] })),
        organizationAPI.getIngredientTemplates(orgId).catch(() => ({ templates: [] })),
        organizationAPI.getOptionTemplates(orgId).catch(() => ({ templates: [] }))
      ]);
      setCategories(catData.categories || []);
      setProducts(prodData.products || []);
      setOrgStores(storesData.stores || []);
      setSizeTemplates(sizeTmpl.templates || []);
      setIngredientTemplates(ingTmpl.templates || []);
      setOptionTemplates(optTmpl.templates || []);
      setLoadState('ready');
    } catch (err: any) {
      console.error('Failed to load catalog:', err);
      showToast('error', t('admin:failedLoadCatalog'));
      setLoadState(err?.response?.status === 403 ? 'forbidden' : 'error');
    } finally {
      setLoading(false);
    }
  }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSyncRequests = useCallback(async () => {
    try {
      const data = await organizationAPI.getOrgSyncRequests(orgId);
      setSyncRequests(data.requests || []);
    } catch (err) {
      console.error('Failed to load sync requests:', err);
    }
  }, [orgId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (subTab === 'sync') loadSyncRequests();
  }, [subTab, loadSyncRequests]);

  // ============================================================
  // Category handlers
  // ============================================================

  const openCategoryModal = (cat?: OrgCategory) => {
    if (cat) {
      setEditingCategory(cat);
      setCategoryForm({
        name: cat.name,
        description: cat.description || '',
        parent_id: cat.parent_id?.toString() || '',
        display_order: cat.display_order,
        is_active: cat.is_active,
        store_ids: cat.store_ids && cat.store_ids.length ? cat.store_ids : null,
        color: cat.color ?? null
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '', parent_id: '', display_order: 0, is_active: true, store_ids: null, color: null });
    }
    setShowCategoryModal(true);
  };

  // «Αυτόματο · <τόνος>»: ο τόνος που θα έδινε ο κανόνας του ταμείου στην κατηγορία της φόρμας — από
  // το όνομα, αλλιώς από τη θέση της ανάμεσα στις ενεργές αδελφές (με τη σειρά του καταλόγου). Στο
  // ταμείο η θέση μετρά στις κατηγορίες ΤΟΥ καταστήματος, άρα για όνομα που δεν «πιάνει» κανόνα
  // είναι εκτίμηση· ένας ρητός τόνος την κάνει ίδια παντού.
  const categoryFormAutoTone = () => {
    const parentId = categoryForm.parent_id ? parseInt(categoryForm.parent_id) : null;
    const siblings = categories.filter(c => (c.parent_id ?? null) === parentId && c.is_active);
    const at = editingCategory ? siblings.findIndex(c => c.id === editingCategory.id) : -1;
    return autoCategoryTone(categoryForm.name.trim() || editingCategory?.name || '', at >= 0 ? at : siblings.length);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) return;
    try {
      setSaving(true);
      const data = {
        name: categoryForm.name.trim(),
        description: categoryForm.description || null,
        parent_id: categoryForm.parent_id ? parseInt(categoryForm.parent_id) : null,
        display_order: categoryForm.display_order,
        is_active: categoryForm.is_active,
        // null = all stores; an array = only those. Mirrors the product flow.
        store_ids: categoryForm.store_ids && categoryForm.store_ids.length ? categoryForm.store_ids : null,
        // Τόνος της παλέτας ή null (= αυτόματο)· κατεβαίνει στα καταστήματα όπως το όνομα.
        color: categoryForm.color || null
      };

      if (editingCategory) {
        await organizationAPI.updateCatalogCategory(orgId, editingCategory.id, data);
        showToast('success', t('admin:categoryUpdatedPending'));
      } else {
        await organizationAPI.createCatalogCategory(orgId, data);
        showToast('success', t('admin:categoryCreatedPending'));
      }

      setShowCategoryModal(false);
      await loadData();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('admin:failedSave2'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (cat: OrgCategory) => {
    try {
      await organizationAPI.deleteCatalogCategory(orgId, cat.id);
      showToast('success', t('admin:categoryDeletedPending'));
      setShowCategoryModal(false);
      await loadData();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('admin:failedDelete2'));
    }
  };

  // ============================================================
  // Product handlers
  // ============================================================

  const openProductModal = (prod?: OrgProduct) => {
    if (prod) {
      setEditingProduct(prod);
      setProductForm({
        category_id: prod.category_id.toString(),
        name: prod.name,
        description: prod.description || '',
        // vat is NUMERIC → pg returns "13.00"; the select options are "13"/"24"/…
        // so normalize via parseFloat or it never matches and shows 24% by default.
        vat: prod.vat != null ? parseFloat(String(prod.vat)).toString() : '24',
        product_type: prod.product_type || 'ΠΡΟΙΟΝ',
        is_available: prod.is_available,
        display_order: prod.display_order,
        vat_take_away: prod.vat_take_away != null ? parseFloat(String(prod.vat_take_away)).toString() : '',
        vat_delivery: prod.vat_delivery != null ? parseFloat(String(prod.vat_delivery)).toString() : '',
        vat_dine_in: prod.vat_dine_in != null ? parseFloat(String(prod.vat_dine_in)).toString() : '',
        fee_category: prod.fee_category != null ? prod.fee_category.toString() : '',
        auto_plastic_fee_count: (prod.auto_plastic_fee_count ?? 0).toString(),
        is_weight_variable: prod.is_weight_variable === true,
        is_open_price: prod.is_open_price === true,
        barcode: prod.barcode || '',
        sell_unit: prod.sell_unit || '',
        max_discount_percent: prod.max_discount_percent != null ? prod.max_discount_percent.toString() : '',
        xenios_product_id: prod.xenios_product_id || '',
        store_ids: prod.store_ids && prod.store_ids.length ? prod.store_ids : null,
        color: prod.color ?? null,
        sizes: (prod.sizes || []).map(s => ({
          id: (s as any).id,
          name: s.name,
          price_modifier: s.price_modifier.toString(),
          is_default: s.is_default,
          is_available: s.is_available
        })),
        simple_ingredients: (prod.simple_ingredients || []).map(i => ({
          ingredient_name: i.ingredient_name,
          extra_price: i.extra_price.toString(),
          is_default: i.is_default,
          category: i.category || ''
        })),
        option_groups: (prod.option_groups || []).map(g => ({
          name: g.name,
          selection_type: g.selection_type === 'multiple' ? 'multiple' as const : 'single' as const,
          is_required: g.is_required,
          template_id: (g as any).template_id ?? null,
          choices: (g.choices || []).map(c => ({
            name: c.name,
            price_modifier: c.price_modifier.toString(),
            is_default: c.is_default
          }))
        }))
      });
      // Ανοιχτές οι ενότητες που έχουν ήδη τιμή, ώστε να μη μένει κρυφό ό,τι ισχύει.
      setChannelVatOpen(prod.vat_take_away != null || prod.vat_delivery != null || prod.vat_dine_in != null);
      setAdvancedOpen(!!prod.barcode || prod.is_weight_variable === true || prod.is_open_price === true);
    } else {
      setEditingProduct(null);
      setProductForm({
        ...emptyProductForm,
        category_id: categories.length > 0 ? categories[0].id.toString() : '',
      });
      setChannelVatOpen(false);
      setAdvancedOpen(false);
    }
    setShowProductModal(true);
  };

  // Επιλογές του payload/των κλειδωμάτων: τα αποθηκευμένα μεγέθη μετράνε ΟΛΑ (και τα μη
  // διαθέσιμα), όπως στο LAST_SIZE του backend.
  const productPayloadOpts: OrgPayloadOptions = {
    editing: editingProduct != null,
    originalSizeCount: editingProduct?.sizes?.length ?? 0,
  };
  const productIsFee = isFeeForm(productForm);
  const productFeeLocked = feeSelectLocked({ ...productPayloadOpts, originalFeeCategory: editingProduct?.fee_category });

  const handleSaveProduct = async () => {
    if (!productForm.name.trim() || !productForm.category_id) return;
    // Μπαγιάτικες τιμές δεν ξαναστέλνονται, ≥1 μέγεθος εκτός τελών, τέλος χωρίς κλειδί sizes (FE-9).
    const built = buildOrgProductPayload(productForm, productPayloadOpts);
    if (built.error) {
      showToast('error', t(`admin:${built.error}`));
      return;
    }
    const data = built.payload;
    try {
      setSaving(true);
      if (editingProduct) {
        await organizationAPI.updateCatalogProduct(orgId, editingProduct.id, data);
        showToast('success', t('admin:productUpdatedPending'));
      } else {
        await organizationAPI.createCatalogProduct(orgId, data);
        showToast('success', t('admin:productCreatedPending'));
      }

      setShowProductModal(false);
      await loadData();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('admin:failedSave2'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (prod: OrgProduct) => {
    try {
      await organizationAPI.deleteCatalogProduct(orgId, prod.id);
      showToast('success', t('admin:productDeletedPending'));
      setShowProductModal(false);
      await loadData();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('admin:failedDelete2'));
    }
  };

  // ============================================================
  // Import from Store
  // ============================================================

  const handleImport = async () => {
    if (!importStoreId) return;
    try {
      setImporting(true);
      const result = await organizationAPI.importFromStore(orgId, parseInt(importStoreId));
      showToast('success', result.message || t('admin:importCompleted'));
      setShowImportModal(false);
      setImportStoreId('');
      await loadData();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('admin:failedImport'));
    } finally {
      setImporting(false);
    }
  };

  // ============================================================
  // Batch Operations
  // ============================================================

  const toggleProductSelection = (id: number) => {
    setSelectedProductIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllProducts = () => {
    if (selectedProductIds.size === filteredProducts.length) {
      setSelectedProductIds(new Set());
    } else {
      setSelectedProductIds(new Set(filteredProducts.map(p => p.id)));
    }
  };

  const handleBatchUpdate = async () => {
    if (selectedProductIds.size === 0 || !batchUpdates.value) return;
    try {
      setBatchProcessing(true);
      const updates: any = {};
      const field = batchUpdates.field;
      const value = batchUpdates.value;

      if (field === 'vat') {
        updates[field] = parseFloat(value);
      } else if (field === 'category_id') {
        updates[field] = parseInt(value);
      } else if (field === 'is_available') {
        updates[field] = value === 'true';
      } else {
        updates[field] = value;
      }

      const targetIds = selectedTargetStores.length > 0 ? selectedTargetStores : undefined;
      const result = await organizationAPI.batchUpdateProducts(orgId, Array.from(selectedProductIds), updates, targetIds);
      showToast('success', result.message || t('admin:batchUpdatedCount', { count: result.updated }));
      setShowBatchModal(false);
      setSelectedProductIds(new Set());
      setBatchUpdates({ field: 'vat', value: '' });
      setSelectedTargetStores([]);
      await loadData();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('admin:failedBatchUpdate'));
    } finally {
      setBatchProcessing(false);
    }
  };

  // ============================================================
  // Selective Sync helpers
  // ============================================================

  const toggleTargetStore = (storeId: number) => {
    setSelectedTargetStores(prev =>
      prev.includes(storeId) ? prev.filter(id => id !== storeId) : [...prev, storeId]
    );
  };

  // ============================================================
  // Size helpers
  // ============================================================

  const addSize = () => {
    setProductForm(prev => ({
      ...prev,
      sizes: [...prev.sizes, { name: '', price_modifier: '0', is_default: prev.sizes.length === 0 }]
    }));
  };

  // «+ Κανονικό»: το μέγεθος «χωρίς μέγεθος» (τιμή 0, default)· η τιμή του μπαίνει στον τιμοκατάλογο.
  const addBaseSizeRow = () => {
    setProductForm(prev => ({ ...prev, sizes: addBaseSize(prev.sizes) }));
  };

  const removeSize = (idx: number) => {
    setProductForm(prev => {
      // Το τελευταίο μέγεθος δεν αφαιρείται (ο backend θα απαντούσε 409 LAST_SIZE)· το X είναι
      // ήδη disabled, αυτό είναι η δεύτερη ασφάλεια.
      if (sizeRemovalLocked(prev, idx, productPayloadOpts)) return prev;
      return { ...prev, sizes: prev.sizes.filter((_, i) => i !== idx) };
    });
  };

  const updateSize = (idx: number, field: string, value: any) => {
    setProductForm(prev => ({
      ...prev,
      sizes: prev.sizes.map((s, i) => i === idx ? { ...s, [field]: value } : s)
    }));
  };

  const applySizeTemplate = (tmpl: any) => {
    const items = (typeof tmpl.items === 'string' ? JSON.parse(tmpl.items) : tmpl.items) || [];
    setProductForm(prev => ({
      ...prev,
      sizes: items.map((i: any) => ({
        name: i.name, price_modifier: String(i.price_modifier || 0), is_default: i.is_default
      }))
    }));
  };

  // ============================================================
  // Simple ingredient helpers
  // ============================================================

  const addIngredient = () => {
    setProductForm(prev => ({
      ...prev,
      simple_ingredients: [...prev.simple_ingredients, { ingredient_name: '', extra_price: '0', is_default: true, category: '' }]
    }));
  };

  const removeIngredient = (idx: number) => {
    setProductForm(prev => ({
      ...prev,
      simple_ingredients: prev.simple_ingredients.filter((_, i) => i !== idx)
    }));
  };

  const updateIngredient = (idx: number, field: string, value: any) => {
    setProductForm(prev => ({
      ...prev,
      simple_ingredients: prev.simple_ingredients.map((ing, i) => i === idx ? { ...ing, [field]: value } : ing)
    }));
  };

  const applyIngredientTemplate = (tmpl: any) => {
    const items = (typeof tmpl.items === 'string' ? JSON.parse(tmpl.items) : tmpl.items) || [];
    setProductForm(prev => ({
      ...prev,
      simple_ingredients: items.map((i: any) => ({
        ingredient_name: i.ingredient_name, extra_price: String(i.extra_price || 0),
        is_default: i.is_default, category: i.category || ''
      }))
    }));
  };

  // ============================================================
  // Option group helpers
  // ============================================================

  const addOptionGroup = () => {
    setProductForm(prev => ({
      ...prev,
      option_groups: [...prev.option_groups, { name: '', selection_type: 'single', is_required: false, choices: [{ name: '', price_modifier: '0', is_default: false }] }]
    }));
  };

  const addOptionGroupFromTemplate = (tmpl: any) => {
    if (!tmpl) return;
    setProductForm(prev => ({
      ...prev,
      option_groups: [...prev.option_groups, {
        name: tmpl.name,
        selection_type: tmpl.selection_type === 'multiple' ? 'multiple' as const : 'single' as const,
        is_required: tmpl.is_required === true,
        template_id: tmpl.id,
        choices: (tmpl.choices || []).map((c: any) => ({
          name: c.name,
          price_modifier: String(c.price_modifier ?? 0),
          is_default: c.is_default === true
        }))
      }]
    }));
  };

  const removeOptionGroup = (gi: number) => {
    setProductForm(prev => ({ ...prev, option_groups: prev.option_groups.filter((_, i) => i !== gi) }));
  };

  const updateOptionGroup = (gi: number, field: string, value: any) => {
    setProductForm(prev => ({
      ...prev,
      option_groups: prev.option_groups.map((g, i) => i === gi ? { ...g, [field]: value } : g)
    }));
  };

  const addChoice = (gi: number) => {
    setProductForm(prev => ({
      ...prev,
      option_groups: prev.option_groups.map((g, i) => i === gi ? { ...g, choices: [...g.choices, { name: '', price_modifier: '0', is_default: false }] } : g)
    }));
  };

  const removeChoice = (gi: number, ci: number) => {
    setProductForm(prev => ({
      ...prev,
      option_groups: prev.option_groups.map((g, i) => i === gi ? { ...g, choices: g.choices.filter((_, j) => j !== ci) } : g)
    }));
  };

  const updateChoice = (gi: number, ci: number, field: string, value: any) => {
    setProductForm(prev => ({
      ...prev,
      option_groups: prev.option_groups.map((g, i) => {
        if (i !== gi) return g;
        let choices = g.choices.map((c, j) => j === ci ? { ...c, [field]: value } : c);
        // For single-select groups, a default choice is exclusive.
        if (field === 'is_default' && value === true && g.selection_type === 'single') {
          choices = choices.map((c, j) => j === ci ? c : { ...c, is_default: false });
        }
        return { ...g, choices };
      })
    }));
  };

  // ============================================================
  // Filtered products
  // ============================================================

  // Αναζήτηση χωρίς τόνους/πεζά-κεφαλαία («κουλουρι» βρίσκει το «ΚΟΥΛΟΎΡΙ») — βρίσκει ό,τι έβρισκε
  // και πριν, και επιπλέον όσα έχαναν οι τόνοι. Η κατηγορία φιλτράρει ΑΚΡΙΒΩΣ (όχι υποκατηγορίες).
  const filteredProducts = products.filter(p => {
    if (selectedCategoryFilter && p.category_id !== selectedCategoryFilter) return false;
    if (search) return matchesSearch(search, p.name, p.description, p.category_name);
    return true;
  });

  // ============================================================
  // Format helpers
  // ============================================================

  const fmtDate = (d: string) => new Date(d).toLocaleString('el-GR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Europe/Athens', hourCycle: 'h23' });

  const syncStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return t('admin:syncStatusPending');
      case 'approved': return t('admin:syncStatusApproved');
      case 'rejected': return t('admin:syncStatusRejected');
      case 'auto_applied': return t('admin:syncStatusAutoApplied');
      default: return status;
    }
  };

  const syncStatusTone = (status: string): 'warn' | 'ok' | 'danger' | 'muted' => {
    switch (status) {
      case 'pending': return 'warn';
      case 'approved': return 'ok';
      case 'rejected': return 'danger';
      default: return 'muted';
    }
  };

  // Parent categories (for select)
  const parentCategories = categories.filter(c => !c.parent_id);
  /** Κατηγορίες με τη σειρά του δέντρου: γονική, οι υποκατηγορίες της, στο τέλος οι ορφανές
   *  (υποκατηγορία χωρίς γονική — δεν πρέπει να συμβαίνει, αλλά να μη χάνεται). */
  const treeOrder = useMemo(() => {
    const out: Array<{ cat: OrgCategory; level: 1 | 2; orphan?: boolean }> = [];
    categories.filter(c => !c.parent_id).forEach(parent => {
      out.push({ cat: parent, level: 1 });
      categories.filter(c => c.parent_id === parent.id).forEach(child => out.push({ cat: child, level: 2 }));
    });
    categories.filter(c => c.parent_id && !categories.find(p => p.id === c.parent_id))
      .forEach(o => out.push({ cat: o, level: 1, orphan: true }));
    return out;
  }, [categories]);

  const storesText = (ids: number[] | null) => (ids && ids.length > 0
    ? t('orgPages:catalog.products.storesCount', { count: ids.length })
    : t('orgPages:catalog.products.storesAll'));

  // ============================================================
  // Render: products
  // ============================================================

  const allSelected = selectedProductIds.size === filteredProducts.length && filteredProducts.length > 0;

  const productColumns: Column<OrgProduct>[] = [
    ...(canManage ? [{
      key: 'select',
      header: (
        <input type="checkbox" className="h-4 w-4 accent-[hsl(var(--brand))]" checked={allSelected}
          ref={el => { if (el) el.indeterminate = selectedProductIds.size > 0 && !allSelected; }}
          onChange={toggleAllProducts} aria-label={t('orgPages:catalog.products.selectAll')} />
      ),
      width: '44px',
      stack: 'end' as const,
      render: (p: OrgProduct) => (
        <input type="checkbox" className="h-4 w-4 accent-[hsl(var(--brand))]" checked={selectedProductIds.has(p.id)}
          onChange={() => toggleProductSelection(p.id)} aria-label={t('orgPages:catalog.products.selectRow', { name: p.name })} />
      ),
    } as Column<OrgProduct>] : []),
    {
      key: 'name',
      header: t('admin:productColumn'),
      width: 'minmax(200px, 2.2fr)',
      stack: 'title',
      render: p => (
        <span className="flex min-w-0 items-center gap-2 font-medium text-t1">
          <CatalogToneDot tone={p.color} />
          <span className="min-w-0 break-words">{p.name}</span>
        </span>
      ),
      stackRender: p => p.name,
    },
    {
      key: 'category',
      header: t('admin:categoryColumn'),
      width: 'minmax(140px, 1.3fr)',
      render: p => <span className="text-t2">{p.category_name}</span>,
      stackRender: p => p.category_name,
    },
    {
      key: 'vat',
      header: t('admin:vatColumn'),
      width: '72px',
      align: 'right',
      render: p => <span className="text-t2">{parseFloat(String(p.vat))}%</span>,
      stackRender: p => `${t('admin:vatColumn')} ${parseFloat(String(p.vat))}%`,
    },
    {
      key: 'status',
      header: t('admin:statusColumn2'),
      width: '112px',
      render: p => (p.is_available
        ? <StatusChip tone="ok">{t('admin:productActive')}</StatusChip>
        : <StatusChip tone="muted">{t('admin:productInactive')}</StatusChip>),
    },
    {
      key: 'stores',
      header: t('orgPages:catalog.products.stores'),
      width: '130px',
      hideBelow: 'narrow',
      render: p => <span className={p.store_ids && p.store_ids.length ? 'text-t1' : 'text-t3'}>{storesText(p.store_ids)}</span>,
    },
    ...(showXeniosField ? [{
      key: 'xenios',
      header: 'Xenios ID',
      width: '130px',
      hideBelow: 'narrow' as const,
      render: (p: OrgProduct) => (p.xenios_product_id
        ? <span className="font-mono text-xs text-t2">{p.xenios_product_id}</span>
        : <span className="text-t4">—</span>),
    } as Column<OrgProduct>] : []),
  ];

  // ============================================================
  // Render: product form (συρτάρι)
  // ============================================================

  const productFormId = `${uid}-product`;
  const productFormBody = (
    <form id={productFormId} className="space-y-7" noValidate
      onSubmit={e => { e.preventDefault(); if (!saving && productForm.name.trim() && productForm.category_id) handleSaveProduct(); }}>
      {/* ── Βασικά ── */}
      <section className="space-y-4">
        <GroupTitle>{t('orgPages:catalog.form.basics')}</GroupTitle>
        <SheetField label={`${t('common:name')} *`} htmlFor={`${uid}-p-name`}>
          <input id={`${uid}-p-name`} type="text" value={productForm.name} autoFocus={!editingProduct}
            onChange={e => setProductForm(f => ({ ...f, name: e.target.value }))}
            className={INPUT_CLS} placeholder={t('admin:productNamePlaceholder')} />
        </SheetField>
        <div className="xs-grid2">
          <SheetField label={`${t('admin:categoryColumn')} *`} htmlFor={`${uid}-p-cat`}>
            <select id={`${uid}-p-cat`} value={productForm.category_id}
              onChange={e => setProductForm(f => ({ ...f, category_id: e.target.value }))} className={SELECT_CLS}>
              <option value="">{t('common:select')}...</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.parent_name ? `${c.parent_name} › ` : ''}{c.name}</option>
              ))}
            </select>
          </SheetField>
          <SheetField label={t('admin:productTypeLabel')} htmlFor={`${uid}-p-type`}>
            <select id={`${uid}-p-type`} value={productForm.product_type}
              onChange={e => setProductForm(f => ({ ...f, product_type: e.target.value }))} className={SELECT_CLS}>
              <option value="ΠΡΟΙΟΝ">{t('admin:typeProduct')}</option>
              <option value="ΕΜΠΟΡΕΥΜΑ">{t('admin:typeGoods')}</option>
              <option value="ΥΠΗΡΕΣΙΑ">{t('admin:typeService')}</option>
            </select>
          </SheetField>
        </div>
        <SheetField label={t('common:description')} htmlFor={`${uid}-p-desc`}>
          <textarea id={`${uid}-p-desc`} value={productForm.description} rows={2}
            onChange={e => setProductForm(f => ({ ...f, description: e.target.value }))}
            className={`${INPUT_CLS} resize-none`} />
        </SheetField>
        {/* Χρώμα της κάρτας στο ταμείο όλων των καταστημάτων (συγχρονίζεται όπως το όνομα). */}
        <CatalogColorPicker
          kind="product"
          idPrefix="org-prod-color"
          compactLabel
          value={productForm.color}
          onChange={tone => setProductForm(f => ({ ...f, color: tone }))}
        />
        {/* Xenios Product ID — only for Bread Factory */}
        {showXeniosField && (
          <SheetField label="Xenios Product ID" htmlFor={`${uid}-p-xenios`}>
            <input id={`${uid}-p-xenios`} type="text" value={productForm.xenios_product_id}
              onChange={e => setProductForm(f => ({ ...f, xenios_product_id: e.target.value }))}
              className={INPUT_CLS} placeholder={t('admin:xeniosIdPlaceholder')} />
          </SheetField>
        )}
      </section>

      {/* ── ΦΠΑ, έκπτωση, τέλη: οι τιμές ορίζονται στους «Τιμοκαταλόγους» ── */}
      <section className="space-y-4">
        <GroupTitle>{t('orgPages:catalog.form.pricing')}</GroupTitle>
        <p className="xs-field-hint !mt-0">{t('orgPages:catalog.form.pricingHint')}</p>
        <div className="xs-grid2">
          <SheetField label={t('admin:vatPercentLabel')} htmlFor={`${uid}-p-vat`}>
            <select id={`${uid}-p-vat`} value={productForm.vat}
              onChange={e => setProductForm(f => ({ ...f, vat: e.target.value }))} className={SELECT_CLS}>
              <option value="24">24%</option>
              <option value="13">13%</option>
              <option value="6">6%</option>
              <option value="0">0%</option>
            </select>
          </SheetField>
          <SheetField label={t('orgPages:catalog.form.maxDiscount')} htmlFor={`${uid}-p-maxd`}>
            <input id={`${uid}-p-maxd`} type="number" step="0.01" min="0" max="100" value={productForm.max_discount_percent}
              onChange={e => setProductForm(f => ({ ...f, max_discount_percent: e.target.value }))}
              className={`${INPUT_CLS} tabular-nums`} placeholder="—" />
          </SheetField>
        </div>
        <Disclosure label={t('orgPages:catalog.form.channelVat')} open={channelVatOpen} onToggle={setChannelVatOpen}>
          <div className="grid grid-cols-3 gap-3">
            {([
              ['vat_take_away', t('orgPages:catalog.form.vatTakeAway')],
              ['vat_delivery', t('orgPages:catalog.form.vatDelivery')],
              ['vat_dine_in', t('orgPages:catalog.form.vatDineIn')],
            ] as const).map(([field, label]) => (
              <SheetField key={field} label={<span className="text-[13px]">{label}</span>} htmlFor={`${uid}-p-${field}`}>
                <input id={`${uid}-p-${field}`} type="number" step="0.01" value={productForm[field]}
                  onChange={e => setProductForm(f => ({ ...f, [field]: e.target.value }))}
                  className={`${COMPACT_INPUT} w-full tabular-nums`} placeholder="—" />
              </SheetField>
            ))}
          </div>
        </Disclosure>
        {/* Πλαστικά / κρατικό τέλος — ορατό (φορολογικά σημαντικό, εμφανίζεται στο καλάθι) */}
        <div className="xs-grid2">
          <SheetField label={t('orgPages:catalog.form.fee')} htmlFor={`${uid}-p-fee`}
            hint={productFeeLocked ? t('admin:feeSelectLockedHint') : undefined}>
            {/* Υπάρχον προϊόν με μεγέθη δεν γίνεται τέλος (FEE_NO_SIZES)· φτιάχνεται νέο προϊόν-τέλος. */}
            <select id={`${uid}-p-fee`} value={productForm.fee_category}
              onChange={e => setProductForm(f => ({ ...f, fee_category: e.target.value }))}
              disabled={productFeeLocked}
              title={productFeeLocked ? t('admin:feeSelectLockedHint') : undefined}
              className={SELECT_CLS}>
              <option value="">{t('orgPages:catalog.form.feeNone')}</option>
              <option value="16">{t('orgPages:catalog.form.fee16')}</option>
              <option value="8">{t('orgPages:catalog.form.fee8')}</option>
            </select>
          </SheetField>
          <SheetField label={t('orgPages:catalog.form.plasticCount')} htmlFor={`${uid}-p-plastic`}>
            <input id={`${uid}-p-plastic`} type="number" min="0" value={productForm.auto_plastic_fee_count}
              onChange={e => setProductForm(f => ({ ...f, auto_plastic_fee_count: e.target.value }))}
              className={`${INPUT_CLS} tabular-nums`} placeholder="0" />
          </SheetField>
        </div>
        <SheetRow
          title={t('admin:availableLabel')}
          subtitle={t('orgPages:catalog.form.availableHint')}
          control={({ labelId, descriptionId }) => (
            <SettingsSwitch checked={productForm.is_available} onChange={next => setProductForm(f => ({ ...f, is_available: next }))}
              labelId={labelId} describedById={descriptionId} />
          )}
        />
      </section>

      {/* ── Διαθεσιμότητα ανά κατάστημα — πιλοτικό σε λίγα, μετά roll-out στα υπόλοιπα ── */}
      {orgStores.length > 0 && (
        <section>
          <StoreScope
            stores={orgStores}
            value={productForm.store_ids}
            onChange={next => setProductForm(f => ({ ...f, store_ids: next }))}
            someHint={t('orgPages:catalog.form.storesSomeHintProduct', { count: (productForm.store_ids || []).length })}
          />
        </section>
      )}

      {/* ── Προχωρημένα: βάρος/ανοιχτή τιμή, barcode ── */}
      <Disclosure label={t('orgPages:catalog.form.advanced')} open={advancedOpen} onToggle={setAdvancedOpen}>
        <div className="space-y-4">
          <SheetField label={t('orgPages:catalog.form.barcode')} htmlFor={`${uid}-p-barcode`}>
            <input id={`${uid}-p-barcode`} type="text" value={productForm.barcode}
              onChange={e => setProductForm(f => ({ ...f, barcode: e.target.value }))} className={INPUT_CLS} placeholder="—" />
          </SheetField>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-t2">
              <input type="checkbox" checked={productForm.is_weight_variable} className="h-4 w-4 accent-[hsl(var(--brand))]"
                onChange={e => setProductForm(f => ({ ...f, is_weight_variable: e.target.checked }))} />
              {t('orgPages:catalog.form.weighted')}
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-t2">
              <input type="checkbox" checked={productForm.is_open_price} className="h-4 w-4 accent-[hsl(var(--brand))]"
                onChange={e => setProductForm(f => ({ ...f, is_open_price: e.target.checked }))} />
              {t('orgPages:catalog.form.openPrice')}
            </label>
          </div>
          {productForm.is_weight_variable && (
            <SheetField label={t('orgPages:catalog.form.sellUnit')} htmlFor={`${uid}-p-unit`} className="max-w-xs">
              <select id={`${uid}-p-unit`} value={productForm.sell_unit}
                onChange={e => setProductForm(f => ({ ...f, sell_unit: e.target.value }))} className={SELECT_CLS}>
                <option value="">—</option>
                <option value="kg">{t('orgPages:catalog.form.unitKg')}</option>
                <option value="liter">{t('orgPages:catalog.form.unitLiter')}</option>
              </select>
            </SheetField>
          )}
        </div>
      </Disclosure>

      {/* ── Μεγέθη — το τέλος δεν έχει μεγέθη (το ποσό του ζει στο state_fees)· τα υπόλοιπα ≥1 (FE-9) ── */}
      <section className="space-y-3">
        <GroupTitle aside={!productIsFee && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <TemplateSelect templates={sizeTemplates} label={t('orgPages:catalog.form.fromTemplateSizes')} onPick={applySizeTemplate} />
            {namedSizes(productForm.sizes).length === 0 && (
              <button type="button" onClick={addBaseSizeRow} className={LINK_BTN}>
                <Plus className="h-3.5 w-3.5" aria-hidden="true" /> {t('admin:addBaseSizeButton')}
              </button>
            )}
            <button type="button" onClick={addSize} className={LINK_BTN}>
              <Plus className="h-3.5 w-3.5" aria-hidden="true" /> {t('admin:addSizeButton')}
            </button>
          </div>
        )}>
          {t('admin:sizesLabel')}
        </GroupTitle>
        {productIsFee ? (
          <p className="text-sm text-t3">{t('admin:feeNoSizesHint')}</p>
        ) : productForm.sizes.length === 0 ? (
          <p className="text-sm text-t3">
            {sizeRequired(productForm, productPayloadOpts) ? t('admin:atLeastOneSizeHint') : t('admin:noSizesHint')}
          </p>
        ) : (
          <ul className="divide-y divide-line border-y border-line">
            {productForm.sizes.map((s, idx) => {
              const locked = sizeRemovalLocked(productForm, idx, productPayloadOpts);
              return (
                <li key={idx} className="flex items-center gap-3 py-2">
                  <input type="text" value={s.name} onChange={e => updateSize(idx, 'name', e.target.value)}
                    placeholder={t('admin:sizeNamePlaceholder')} aria-label={t('orgPages:catalog.form.sizeName', { index: idx + 1 })}
                    className={`${COMPACT_INPUT} flex-1`} />
                  <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-sm text-t2">
                    <input type="radio" name={`${uid}-default-size`} checked={s.is_default} className="h-4 w-4 accent-[hsl(var(--brand))]"
                      aria-label={t('orgPages:catalog.form.defaultOf', { name: s.name || idx + 1 })}
                      onChange={() => {
                        setProductForm(prev => ({ ...prev, sizes: prev.sizes.map((sz, i) => ({ ...sz, is_default: i === idx })) }));
                      }} />
                    {t('orgPages:catalog.form.default')}
                  </label>
                  <button type="button" onClick={() => removeSize(idx)} disabled={locked}
                    title={locked ? t('admin:lastSizeLocked') : t('orgPages:catalog.form.remove')}
                    aria-label={t('orgPages:catalog.form.remove')} className={ICON_BTN}>
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ── Υλικά / Extras ── */}
      <section className="space-y-3">
        <GroupTitle aside={(
          <div className="flex flex-wrap items-center justify-end gap-2">
            <TemplateSelect templates={ingredientTemplates} label={t('orgPages:catalog.form.fromTemplateIngredients')} onPick={applyIngredientTemplate} />
            <button type="button" onClick={addIngredient} className={LINK_BTN}>
              <Plus className="h-3.5 w-3.5" aria-hidden="true" /> {t('admin:addSizeButton')}
            </button>
          </div>
        )}>
          {t('admin:ingredientsLabel')}
        </GroupTitle>
        {productForm.simple_ingredients.length === 0 ? (
          <p className="text-sm text-t3">{t('admin:noIngredientsHint')}</p>
        ) : (
          <ul className="divide-y divide-line border-y border-line">
            {productForm.simple_ingredients.map((ing, idx) => (
              <li key={idx} className="flex items-center gap-3 py-2">
                <input type="text" value={ing.ingredient_name} onChange={e => updateIngredient(idx, 'ingredient_name', e.target.value)}
                  placeholder={t('admin:ingredientNamePlaceholder')} aria-label={t('orgPages:catalog.form.ingredientName', { index: idx + 1 })}
                  className={`${COMPACT_INPUT} flex-1`} />
                <input type="number" step="0.01" value={ing.extra_price} onChange={e => updateIngredient(idx, 'extra_price', e.target.value)}
                  placeholder="0.00" aria-label={t('orgPages:catalog.form.ingredientPrice', { index: idx + 1 })}
                  className={`${COMPACT_INPUT} w-24 text-right tabular-nums`} />
                <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-sm text-t2">
                  <input type="checkbox" checked={ing.is_default} className="h-4 w-4 accent-[hsl(var(--brand))]"
                    onChange={e => updateIngredient(idx, 'is_default', e.target.checked)} />
                  {t('orgPages:catalog.form.default')}
                </label>
                <button type="button" onClick={() => removeIngredient(idx)} aria-label={t('orgPages:catalog.form.remove')}
                  title={t('orgPages:catalog.form.remove')} className={ICON_BTN}>
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Επιλογές (ομάδες μονής / πολλαπλής επιλογής) ── */}
      <section className="space-y-3">
        <GroupTitle aside={(
          <div className="flex flex-wrap items-center justify-end gap-2">
            <TemplateSelect templates={optionTemplates} label={t('orgPages:catalog.form.fromTemplateOptions')} onPick={addOptionGroupFromTemplate} />
            <button type="button" onClick={addOptionGroup} className={LINK_BTN}>
              <Plus className="h-3.5 w-3.5" aria-hidden="true" /> {t('orgPages:catalog.form.newGroup')}
            </button>
          </div>
        )}>
          {t('orgPages:catalog.form.options')}
        </GroupTitle>
        {productForm.option_groups.length === 0 ? (
          <p className="text-sm text-t3">{t('orgPages:catalog.form.optionsHint')}</p>
        ) : (
          <div className="space-y-3">
            {productForm.option_groups.map((g, gi) => (
              <div key={gi} className="space-y-3 rounded-xl border border-line p-3">
                {g.template_id && (
                  <Banner tone="info" icon={Layers} action={(
                    <button type="button" onClick={() => updateOptionGroup(gi, 'template_id', null)} className={LINK_BTN}>
                      {t('orgPages:catalog.form.unlink')}
                    </button>
                  )}>
                    <span className="text-[13px]">{t('orgPages:catalog.form.fromTemplateNote')}</span>
                  </Banner>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <input type="text" value={g.name} onChange={e => updateOptionGroup(gi, 'name', e.target.value)}
                    placeholder={t('orgPages:catalog.form.groupName')} aria-label={t('orgPages:catalog.form.groupName')}
                    className={`${COMPACT_INPUT} min-w-[10rem] flex-1 font-medium`} />
                  <select value={g.selection_type} onChange={e => updateOptionGroup(gi, 'selection_type', e.target.value)}
                    aria-label={t('orgPages:catalog.form.options')} className={`${COMPACT_INPUT} pr-7`}>
                    <option value="single">{t('orgPages:catalog.form.single')}</option>
                    <option value="multiple">{t('orgPages:catalog.form.multiple')}</option>
                  </select>
                  <label className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm text-t2">
                    <input type="checkbox" checked={g.is_required} className="h-4 w-4 accent-[hsl(var(--brand))]"
                      onChange={e => updateOptionGroup(gi, 'is_required', e.target.checked)} />
                    {t('orgPages:catalog.form.required')}
                  </label>
                  <button type="button" onClick={() => removeOptionGroup(gi)} aria-label={t('orgPages:catalog.form.removeGroup')}
                    title={t('orgPages:catalog.form.removeGroup')} className={ICON_BTN}>
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
                <ul className="divide-y divide-line border-l-2 border-line pl-3">
                  {g.choices.map((c, ci) => (
                    <li key={ci} className="flex items-center gap-2 py-1.5">
                      <input type="text" value={c.name} onChange={e => updateChoice(gi, ci, 'name', e.target.value)}
                        placeholder={t('orgPages:catalog.form.choiceName')} aria-label={t('orgPages:catalog.form.choiceName')}
                        className={`${COMPACT_INPUT} flex-1`} />
                      <input type="number" step="0.01" value={c.price_modifier} onChange={e => updateChoice(gi, ci, 'price_modifier', e.target.value)}
                        placeholder="0.00" aria-label={t('orgPages:catalog.form.choicePrice')}
                        className={`${COMPACT_INPUT} w-20 text-right tabular-nums`} />
                      <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-sm text-t2">
                        <input type="checkbox" checked={c.is_default} className="h-4 w-4 accent-[hsl(var(--brand))]"
                          onChange={e => updateChoice(gi, ci, 'is_default', e.target.checked)} />
                        {t('orgPages:catalog.form.default')}
                      </label>
                      <button type="button" onClick={() => removeChoice(gi, ci)} aria-label={t('orgPages:catalog.form.remove')}
                        title={t('orgPages:catalog.form.remove')} className={ICON_BTN}>
                        <X className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                  <li className="pt-2">
                    <button type="button" onClick={() => addChoice(gi)} className={LINK_BTN}>
                      <Plus className="h-3.5 w-3.5" aria-hidden="true" /> {t('orgPages:catalog.form.addChoice')}
                    </button>
                  </li>
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>
    </form>
  );

  // ============================================================
  // Render: category form (συρτάρι)
  // ============================================================

  const categoryFormId = `${uid}-category`;
  const categoryFormBody = (
    <form id={categoryFormId} className="space-y-5" noValidate
      onSubmit={e => { e.preventDefault(); if (!saving && categoryForm.name.trim()) handleSaveCategory(); }}>
      <SheetField label={`${t('common:name')} *`} htmlFor={`${uid}-c-name`}>
        <input id={`${uid}-c-name`} type="text" value={categoryForm.name} autoFocus={!editingCategory}
          onChange={e => setCategoryForm(f => ({ ...f, name: e.target.value }))}
          className={INPUT_CLS} placeholder={t('admin:categoryNamePlaceholder')} />
      </SheetField>
      <SheetField label={t('common:description')} htmlFor={`${uid}-c-desc`}>
        <input id={`${uid}-c-desc`} type="text" value={categoryForm.description}
          onChange={e => setCategoryForm(f => ({ ...f, description: e.target.value }))} className={INPUT_CLS} />
      </SheetField>
      <div className="xs-grid2">
        <SheetField label={t('admin:parentCategoryLabel')} htmlFor={`${uid}-c-parent`}>
          <select id={`${uid}-c-parent`} value={categoryForm.parent_id}
            onChange={e => setCategoryForm(f => ({ ...f, parent_id: e.target.value }))} className={SELECT_CLS}>
            <option value="">{t('admin:noParentOption')}</option>
            {parentCategories.filter(c => c.id !== editingCategory?.id).map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </SheetField>
        <SheetField label={t('admin:displayOrderLabel')} htmlFor={`${uid}-c-order`}>
          <input id={`${uid}-c-order`} type="number" value={categoryForm.display_order}
            onChange={e => setCategoryForm(f => ({ ...f, display_order: parseInt(e.target.value) || 0 }))}
            className={`${INPUT_CLS} tabular-nums`} />
        </SheetField>
      </div>
      <SheetRow
        title={t('admin:activeLabel')}
        subtitle={t('orgPages:catalog.form.activeHint')}
        control={({ labelId, descriptionId }) => (
          <SettingsSwitch checked={categoryForm.is_active} onChange={next => setCategoryForm(f => ({ ...f, is_active: next }))}
            labelId={labelId} describedById={descriptionId} />
        )}
      />
      {/* Χρώμα του κουτιού στο ταμείο όλων των καταστημάτων (συγχρονίζεται όπως το όνομα). */}
      <CatalogColorPicker
        kind="category"
        idPrefix="org-cat-color"
        compactLabel
        value={categoryForm.color}
        autoTone={categoryFormAutoTone()}
        onChange={tone => setCategoryForm(f => ({ ...f, color: tone }))}
      />
      {/* Διαθεσιμότητα ανά κατάστημα — ίδια λογική με τα προϊόντα */}
      {orgStores.length > 0 && (
        <StoreScope
          stores={orgStores}
          value={categoryForm.store_ids}
          onChange={next => setCategoryForm(f => ({ ...f, store_ids: next }))}
          someHint={t('orgPages:catalog.form.storesSomeHintCategory', { count: (categoryForm.store_ids || []).length })}
        />
      )}
    </form>
  );

  // ============================================================
  // Render: batch form (συρτάρι)
  // ============================================================

  const batchFormId = `${uid}-batch`;
  const batchFormBody = (
    <form id={batchFormId} className="space-y-5" noValidate
      onSubmit={e => { e.preventDefault(); if (!batchProcessing && batchUpdates.value) handleBatchUpdate(); }}>
      {/* What to change */}
      <div className="xs-grid2">
        <SheetField label={t('admin:batchFieldLabel')} htmlFor={`${uid}-b-field`} hint={t('orgPages:catalog.batch.pricesHint')}>
          <select id={`${uid}-b-field`} value={batchUpdates.field}
            onChange={e => setBatchUpdates(prev => ({ ...prev, field: e.target.value, value: '' }))} className={SELECT_CLS}>
            <option value="vat">{t('admin:fieldLabels.vat')}</option>
            <option value="category_id">{t('admin:fieldLabels.category_name')}</option>
            <option value="is_available">{t('admin:availabilityOption')}</option>
          </select>
        </SheetField>
        <SheetField label={t('admin:batchNewValueLabel')} htmlFor={`${uid}-b-value`}>
          {batchUpdates.field === 'category_id' ? (
            <select id={`${uid}-b-value`} value={batchUpdates.value}
              onChange={e => setBatchUpdates(prev => ({ ...prev, value: e.target.value }))} className={SELECT_CLS}>
              <option value="">{t('common:select')}...</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.parent_name ? `${c.parent_name} › ` : ''}{c.name}</option>
              ))}
            </select>
          ) : batchUpdates.field === 'is_available' ? (
            <select id={`${uid}-b-value`} value={batchUpdates.value}
              onChange={e => setBatchUpdates(prev => ({ ...prev, value: e.target.value }))} className={SELECT_CLS}>
              <option value="">{t('common:select')}...</option>
              <option value="true">{t('admin:productActive')}</option>
              <option value="false">{t('admin:productInactive')}</option>
            </select>
          ) : batchUpdates.field === 'vat' ? (
            <select id={`${uid}-b-value`} value={batchUpdates.value}
              onChange={e => setBatchUpdates(prev => ({ ...prev, value: e.target.value }))} className={SELECT_CLS}>
              <option value="">{t('common:select')}...</option>
              <option value="24">24%</option>
              <option value="13">13%</option>
              <option value="6">6%</option>
              <option value="0">0%</option>
            </select>
          ) : (
            <input id={`${uid}-b-value`} type="number" step="0.01" value={batchUpdates.value}
              onChange={e => setBatchUpdates(prev => ({ ...prev, value: e.target.value }))} className={INPUT_CLS} placeholder="0.00" />
          )}
        </SheetField>
      </div>

      {/* Selective sync: target stores */}
      {orgStores.length > 0 && (
        <SheetField
          label={(
            <span className="flex w-full items-center justify-between gap-3">
              {t('admin:batchSyncTargetLabel')}
              <span className="text-xs font-normal text-t3">
                {selectedTargetStores.length === 0 ? t('admin:allStoresOption') : t('admin:selectedStoresCount', { count: selectedTargetStores.length })}
              </span>
            </span>
          )}
          hint={t('admin:emptySelectionAllStores')}
        >
          <div className="flex flex-wrap gap-1.5">
            {orgStores.map(s => {
              const on = selectedTargetStores.includes(s.id);
              return (
                <button key={s.id} type="button" aria-pressed={on} className={chipClass(on)} onClick={() => toggleTargetStore(s.id)}>
                  {s.name} <span className="text-xs font-normal text-t4 tabular-nums">#{s.id}</span>
                </button>
              );
            })}
          </div>
        </SheetField>
      )}
    </form>
  );

  // ============================================================
  // Render: products sub-tab (δέντρο | πίνακας)
  // ============================================================

  const productsTab = (
    <ProductsLayout
      tree={(
        <CategoryTree
          treeOrder={treeOrder}
          products={products}
          selected={selectedCategoryFilter}
          onSelect={id => setSelectedCategoryFilter(id)}
          canManage={canManage}
          onEdit={cat => openCategoryModal(cat)}
          onAdd={() => openCategoryModal()}
        />
      )}
      categoryFilter={(
        <FilterSelect
          label={t('admin:categoryColumn')}
          value={selectedCategoryFilter ? String(selectedCategoryFilter) : ''}
          onChange={v => setSelectedCategoryFilter(v ? parseInt(v) : null)}
          allLabel={t('admin:allCategoriesOption')}
          options={categories.map(c => ({ value: String(c.id), label: `${c.parent_name ? `${c.parent_name} › ` : ''}${c.name}` }))}
          testId="org-catalog-category-filter"
        />
      )}
    >
      {(showTreeFilter) => (
        <>
          <ListToolbar
            search={{ value: search, onChange: setSearch, placeholder: t('admin:searchProducts'), ariaLabel: t('admin:searchProducts') }}
            filters={showTreeFilter}
            actions={canManage ? (
              <button type="button" className={PRIMARY_BTN} onClick={() => openProductModal()}>
                <Plus className="h-4 w-4" aria-hidden="true" /> {t('admin:newProduct')}
              </button>
            ) : null}
            summary={loadState === 'ready' && !loading
              ? (filteredProducts.length === products.length
                ? t('orgPages:catalog.products.count', { count: products.length })
                : t('orgPages:catalog.products.shown', { shown: filteredProducts.length, total: products.length }))
              : null}
          />

          {/* Batch action bar */}
          {canManage && selectedProductIds.size > 0 && (
            <div role="status" className="mb-3 flex flex-wrap items-center gap-3 rounded-xl bg-brand-soft px-4 py-2 text-sm text-brand">
              <span className="font-semibold">{t('admin:selectedProductsCount', { count: selectedProductIds.size })}</span>
              <span className="flex-1" />
              <button type="button" onClick={() => setShowBatchModal(true)} className={`${PRIMARY_BTN} min-h-[40px]`}>
                <Pencil className="h-4 w-4" aria-hidden="true" /> {t('admin:batchChange')}
              </button>
              <button type="button" onClick={() => setSelectedProductIds(new Set())} className={`${GHOST_BTN} min-h-[40px]`}>
                {t('common:cancel')}
              </button>
            </div>
          )}

          {loadState === 'error' || loadState === 'forbidden' ? (
            <LoadFailed message={loadState === 'forbidden' ? t('orgPages:common.forbidden') : t('orgPages:catalog.loadFailed')} onRetry={loadData} />
          ) : (
            <DataTable
              columns={productColumns}
              rows={filteredProducts}
              rowKey={p => p.id}
              onRowClick={canManage ? p => openProductModal(p) : undefined}
              selectedKey={showProductModal && editingProduct ? editingProduct.id : null}
              loading={loading}
              rowLabel={p => t('orgPages:catalog.products.rowLabel', { name: p.name })}
              rowClassName={p => (selectedProductIds.has(p.id) ? 'bg-brand-soft/60' : undefined)}
              ariaLabel={t('admin:productsTabLabel')}
              empty={(
                <EmptyState
                  icon={Package}
                  title={products.length === 0 ? t('admin:emptyCatalog') : t('admin:noProductsFound')}
                  hint={products.length === 0
                    ? (canManage ? t('orgPages:catalog.products.emptyHint') : undefined)
                    : t('orgPages:catalog.products.noMatchHint')}
                />
              )}
              testId="org-products-table"
            />
          )}
        </>
      )}
    </ProductsLayout>
  );

  // ============================================================
  // Render: categories sub-tab
  // ============================================================

  const categoryColumns: Column<{ cat: OrgCategory; level: 1 | 2; orphan?: boolean }>[] = [
    {
      key: 'name',
      header: t('orgPages:catalog.categories.column'),
      width: 'minmax(220px, 2.4fr)',
      render: ({ cat, level, orphan }) => (
        <span className={`flex min-w-0 items-center gap-2 ${level === 2 ? 'pl-7' : ''}`}>
          {level === 2 && <span aria-hidden="true" className="-ml-4 h-px w-3 shrink-0 bg-line-strong" />}
          <CatalogToneDot tone={cat.color} />
          <span className={`min-w-0 break-words ${level === 1 ? 'font-medium text-t1' : 'text-t2'}`}>{cat.name}</span>
          {orphan && <StatusChip tone="warn">{t('orgPages:catalog.categories.orphan')}</StatusChip>}
        </span>
      ),
      stackRender: ({ cat, level }) => (level === 2 && cat.parent_name ? `${cat.parent_name} › ${cat.name}` : cat.name),
    },
    {
      key: 'products',
      header: t('orgPages:catalog.categories.products'),
      width: '110px',
      align: 'right',
      render: ({ cat }) => <span className="text-t2">{cat.product_count}</span>,
      stackRender: ({ cat }) => t('orgPages:catalog.products.count', { count: cat.product_count }),
    },
    {
      key: 'stores',
      header: t('orgPages:catalog.categories.stores'),
      width: '140px',
      hideBelow: 'narrow',
      render: ({ cat }) => <span className={cat.store_ids && cat.store_ids.length ? 'text-t1' : 'text-t3'}>{storesText(cat.store_ids)}</span>,
    },
    {
      key: 'status',
      header: t('orgPages:catalog.categories.status'),
      width: '120px',
      render: ({ cat }) => (cat.is_active
        ? <StatusChip tone="ok">{t('orgPages:catalog.categories.active')}</StatusChip>
        : <StatusChip tone="muted">{t('admin:categoryInactive')}</StatusChip>),
    },
  ];

  const categoriesTab = (
    <>
      <ListToolbar
        actions={canManage ? (
          <button type="button" className={PRIMARY_BTN} onClick={() => openCategoryModal()}>
            <Plus className="h-4 w-4" aria-hidden="true" /> {t('admin:newCategoryButton')}
          </button>
        ) : null}
        summary={loadState === 'ready' && !loading ? t('orgPages:catalog.categories.count', { count: categories.length }) : null}
      />
      {loadState === 'error' || loadState === 'forbidden' ? (
        <LoadFailed message={loadState === 'forbidden' ? t('orgPages:common.forbidden') : t('orgPages:catalog.loadFailed')} onRetry={loadData} />
      ) : (
        <DataTable
          columns={categoryColumns}
          rows={treeOrder}
          rowKey={r => r.cat.id}
          onRowClick={canManage ? r => openCategoryModal(r.cat) : undefined}
          selectedKey={showCategoryModal && editingCategory ? editingCategory.id : null}
          loading={loading}
          rowLabel={r => t('orgPages:catalog.categories.rowLabel', { name: r.cat.name })}
          rowClassName={r => (r.cat.is_active ? undefined : 'opacity-70')}
          ariaLabel={t('admin:categoriesTabLabel')}
          empty={<EmptyState icon={Tag} title={t('admin:noCategoriesEmpty')} />}
          testId="org-categories-table"
        />
      )}
    </>
  );

  // ============================================================
  // Render: sync sub-tab
  // ============================================================

  const syncTab = (() => {
    const pendingCount = syncRequests.filter(s => s.status === 'pending').length;
    const doneCount = syncRequests.length - pendingCount;
    const storesPending = new Set(syncRequests.filter(s => s.status === 'pending').map(s => s.store_id)).size;
    const rank = (s: string) => (s === 'pending' ? 0 : 1);
    const rows = syncRequests
      .filter(sr => syncFilter === 'all' || sr.status === 'pending')
      .sort((a, b) => rank(a.status) - rank(b.status) || (a.created_at < b.created_at ? 1 : -1));
    const syncColumns: Column<SyncRequest>[] = [
      {
        key: 'summary',
        header: t('admin:syncChangeColumn'),
        width: 'minmax(240px, 2.6fr)',
        render: sr => (
          <>
            <span className="block text-t1">{sr.change_summary}</span>
            <span className="block text-xs text-t3">{t('admin:fromUser', { name: sr.created_by_name || '—' })}</span>
          </>
        ),
        stackRender: sr => sr.change_summary,
      },
      { key: 'store', header: t('common:store'), width: 'minmax(140px, 1.2fr)', render: sr => <span className="text-t2">{sr.store_name}</span> },
      {
        key: 'status',
        header: t('admin:statusColumn2'),
        width: '130px',
        render: sr => <StatusChip tone={syncStatusTone(sr.status)}>{syncStatusLabel(sr.status)}</StatusChip>,
      },
      {
        key: 'date',
        header: t('common:date'),
        width: '150px',
        align: 'right',
        render: sr => <span className="text-t3 tabular-nums">{fmtDate(sr.created_at)}</span>,
      },
    ];
    return (
      <>
        <ListToolbar
          filters={(
            <div className="lp-seg" role="radiogroup" aria-label={t('orgPages:catalog.sync.filterLabel')}>
              <button type="button" role="radio" aria-checked={syncFilter === 'pending'} onClick={() => setSyncFilter('pending')}>
                {t('orgPages:catalog.sync.filterPending')}
                {pendingCount > 0 && <span className="ml-1.5 tabular-nums text-t3">{pendingCount}</span>}
              </button>
              <button type="button" role="radio" aria-checked={syncFilter === 'all'} onClick={() => setSyncFilter('all')}>
                {t('orgPages:catalog.sync.filterAll')}
                <span className="ml-1.5 tabular-nums text-t3">{syncRequests.length}</span>
              </button>
            </div>
          )}
          actions={(
            <button type="button" onClick={loadSyncRequests} className={GHOST_BTN}>
              <RefreshCw className="h-4 w-4" aria-hidden="true" /> {t('common:refresh')}
            </button>
          )}
          summary={pendingCount > 0
            ? t('orgPages:catalog.sync.pending', {
              count: pendingCount, stores: storesPending,
              storesWord: storesPending === 1 ? t('orgPages:catalog.sync.storeOne') : t('orgPages:catalog.sync.storeMany'),
            })
            : t('orgPages:catalog.sync.allClear')}
        />
        <DataTable
          columns={syncColumns}
          rows={rows}
          rowKey={sr => sr.id}
          rowLabel={sr => t('orgPages:catalog.sync.rowLabel', { summary: sr.change_summary })}
          ariaLabel={t('admin:syncTabLabel')}
          empty={(
            <EmptyState
              icon={RefreshCw}
              title={syncFilter === 'pending' && syncRequests.length > 0 ? t('orgPages:catalog.sync.nonePending') : t('admin:noSyncRequests')}
              action={syncFilter === 'pending' && doneCount > 0 ? (
                <button type="button" onClick={() => setSyncFilter('all')} className={GHOST_BTN}>
                  {t('orgPages:catalog.sync.showHistory', { count: doneCount })}
                </button>
              ) : undefined}
            />
          )}
          testId="org-sync-table"
        />
      </>
    );
  })();

  // ============================================================
  // Render
  // ============================================================

  const tabs = [
    { id: 'products' as const, label: t('admin:productsTabLabel'), count: loadState === 'ready' ? products.length : null },
    { id: 'categories' as const, label: t('admin:categoriesTabLabel'), count: loadState === 'ready' ? categories.length : null },
    { id: 'pricelists' as const, label: t('orgPages:catalog.tabs.priceLists') },
    { id: 'templates' as const, label: t('admin:templatesTabLabel') },
    { id: 'sync' as const, label: t('admin:syncTabLabel') },
    { id: 'consistency' as const, label: t('orgPages:catalog.tabs.consistency') },
  ];

  const productTitle = editingProduct ? (productForm.name.trim() || editingProduct.name) : t('admin:newProductTitle');

  return (
    <ListPage
      eyebrow={t('orgDashboard:nav.groups.catalog')}
      title={t('orgPages:catalog.title')}
      subtitle={t('orgPages:catalog.subtitle')}
      // Η «Ανανέωση» ξαναφορτώνει προϊόντα/κατηγορίες: φαίνεται μόνο εκεί — τιμοκατάλογοι, πρότυπα, αιτήματα και
      // συμφωνία έχουν τη δική τους (ως 14/10 ένα εικονίδιο σε όλες τις καρτέλες, που εκεί δεν άλλαζε τίποτα ορατό).
      actions={subTab === 'products' || subTab === 'categories' ? (
        <>
          {/* Εισαγωγή καταλόγου από κατάστημα — προσωρινά κρυμμένο (μπερδεύει με τη ροή master καταλόγου)
          {canManage && orgStores.length > 0 && (
            <button type="button" onClick={() => setShowImportModal(true)} className={GHOST_BTN} title={t('admin:importCatalogTooltip')}>
              {t('admin:importButton')}
            </button>
          )}
          */}
          <button type="button" onClick={loadData} className={GHOST_BTN} title={t('common:refresh')} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" /> {t('common:refresh')}
          </button>
        </>
      ) : undefined}
      testId="org-catalog"
    >
      <FlashToast toast={toast} />

      <div className="pb-4">
        <SubTabs<SubTabId> label={t('orgPages:catalog.tabsLabel')} tabs={tabs} value={subTab} onChange={setSubTab} testId="org-catalog-tabs" />
      </div>

      {subTab === 'products' && productsTab}
      {subTab === 'categories' && categoriesTab}

      {/* ============================================================ */}
      {/* TEMPLATES TAB */}
      {/* ============================================================ */}
      {subTab === 'templates' && (
        <OrgTemplatesManager orgId={orgId} canManage={canManage} />
      )}

      {subTab === 'pricelists' && (
        <OrgPriceListsManager orgId={orgId} canManage={canManage} />
      )}

      {/* ============================================================ */}
      {/* CONSISTENCY TAB — κεντρικός κατάλογος ↔ καταλόγοι καταστημάτων */}
      {/* ============================================================ */}
      {subTab === 'consistency' && (
        <OrgCatalogConsistency orgId={orgId} canManage={canManage} />
      )}

      {subTab === 'sync' && syncTab}

      {/* ============================================================ */}
      {/* CATEGORY PANEL */}
      {/* ============================================================ */}
      <DetailPanel
        open={showCategoryModal}
        onClose={() => {
          if (deleteCategoryTarget) { setDeleteCategoryTarget(null); return; }
          setShowCategoryModal(false);
        }}
        title={editingCategory ? (categoryForm.name.trim() || editingCategory.name) : t('admin:newCategoryTitle')}
        subtitle={editingCategory ? (editingCategory.parent_name
          ? t('orgPages:catalog.categories.subOf', { name: editingCategory.parent_name })
          : t('admin:editCategoryTitle')) : undefined}
        footer={(
          <>
            <button type="button" onClick={() => setShowCategoryModal(false)} className={GHOST_BTN}>{t('common:cancel')}</button>
            <button type="submit" form={categoryFormId} disabled={saving || !categoryForm.name.trim()} className={PRIMARY_BTN}>
              {editingCategory ? t('common:save') : t('admin:createButton')}
            </button>
          </>
        )}
        footerStart={editingCategory && canManage ? (
          <button type="button" onClick={() => setDeleteCategoryTarget(editingCategory)} className={DANGER_GHOST_BTN}>
            <Trash2 className="h-4 w-4" aria-hidden="true" /> {t('common:delete')}
          </button>
        ) : undefined}
        testId="org-category-panel"
      >
        {showCategoryModal && categoryFormBody}
      </DetailPanel>

      {/* ============================================================ */}
      {/* PRODUCT PANEL */}
      {/* ============================================================ */}
      <DetailPanel
        open={showProductModal}
        width="lg"
        onClose={() => {
          if (deleteProductTarget) { setDeleteProductTarget(null); return; }
          setShowProductModal(false);
        }}
        title={productTitle}
        subtitle={editingProduct ? `${t('admin:editProductModalTitle')} · ${editingProduct.category_name}` : undefined}
        footer={(
          <>
            <button type="button" onClick={() => setShowProductModal(false)} className={GHOST_BTN}>{t('common:cancel')}</button>
            <button type="submit" form={productFormId} disabled={saving || !productForm.name.trim() || !productForm.category_id} className={PRIMARY_BTN}>
              {editingProduct ? t('common:save') : t('admin:createButton')}
            </button>
          </>
        )}
        footerStart={editingProduct && canManage ? (
          <button type="button" onClick={() => setDeleteProductTarget(editingProduct)} className={DANGER_GHOST_BTN}>
            <Trash2 className="h-4 w-4" aria-hidden="true" /> {t('common:delete')}
          </button>
        ) : undefined}
        testId="org-product-panel"
      >
        {showProductModal && productFormBody}
      </DetailPanel>

      {/* ============================================================ */}
      {/* IMPORT FROM STORE (το κουμπί είναι προσωρινά κρυμμένο, βλ. επάνω) */}
      {/* ============================================================ */}
      <DetailPanel
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title={t('admin:importFromStoreTitle')}
        footer={(
          <>
            <button type="button" onClick={() => setShowImportModal(false)} className={GHOST_BTN}>{t('common:cancel')}</button>
            <button type="button" onClick={handleImport} disabled={importing || !importStoreId} className={PRIMARY_BTN}>
              {t('admin:importButton')}
            </button>
          </>
        )}
        testId="org-import-panel"
      >
        <div className="space-y-4">
          <p className="text-sm text-t3">{t('admin:importDescription')}</p>
          <SheetField label={t('admin:sourceStore')} htmlFor={`${uid}-import-store`}>
            <select id={`${uid}-import-store`} value={importStoreId} onChange={e => setImportStoreId(e.target.value)} className={SELECT_CLS}>
              <option value="">{t('admin:selectStorePlaceholder')}</option>
              {orgStores.map(s => (
                <option key={s.id} value={s.id}>{s.name} (#{s.id})</option>
              ))}
            </select>
          </SheetField>
        </div>
      </DetailPanel>

      {/* ============================================================ */}
      {/* BATCH UPDATE PANEL */}
      {/* ============================================================ */}
      <DetailPanel
        open={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        title={t('admin:batchModalTitle', { count: selectedProductIds.size })}
        footer={(
          <>
            <button type="button" onClick={() => setShowBatchModal(false)} className={GHOST_BTN}>{t('common:cancel')}</button>
            <button type="submit" form={batchFormId} disabled={batchProcessing || !batchUpdates.value} className={PRIMARY_BTN}>
              {t('admin:applyToProducts', { count: selectedProductIds.size })}
            </button>
          </>
        )}
        testId="org-batch-panel"
      >
        {showBatchModal && batchFormBody}
      </DetailPanel>

      <ConfirmModal
        isOpen={!!deleteProductTarget}
        variant="danger"
        title={t('orgPages:catalog.products.deleteTitle')}
        message={deleteProductTarget ? t('admin:deleteProductConfirm', { name: deleteProductTarget.name }) : ''}
        confirmLabel={t('common:delete')}
        cancelLabel={t('common:cancel')}
        onConfirm={() => { const p = deleteProductTarget; setDeleteProductTarget(null); if (p) handleDeleteProduct(p); }}
        onCancel={() => setDeleteProductTarget(null)}
      />
      <ConfirmModal
        isOpen={!!deleteCategoryTarget}
        variant="danger"
        title={t('orgPages:catalog.categories.deleteTitle')}
        message={deleteCategoryTarget ? t('admin:deleteCategoryConfirm', { name: deleteCategoryTarget.name }) : ''}
        confirmLabel={t('common:delete')}
        cancelLabel={t('common:cancel')}
        onConfirm={() => { const c = deleteCategoryTarget; setDeleteCategoryTarget(null); if (c) handleDeleteCategory(c); }}
        onCancel={() => setDeleteCategoryTarget(null)}
      />
    </ListPage>
  );
};

// ============================================================
// Δέντρο κατηγοριών (φίλτρο της λίστας προϊόντων)
// ============================================================

/**
 * Η στήλη αριστερά, όπως στον «Κατάλογο» του καταστήματος: «Όλα τα προϊόντα» και οι κατηγορίες με τις
 * υποκατηγορίες τους, κουκκίδα τόνου και πλήθος. Πάτημα = φίλτρο της λίστας (ΑΚΡΙΒΩΣ η κατηγορία,
 * όπως το παλιό dropdown)· το ✎ ανοίγει την κατηγορία στο συρτάρι. Το πλήθος μετρά τα φορτωμένα
 * προϊόντα, για να λέει ό,τι θα δείξει η λίστα.
 */
const CategoryTree: React.FC<{
  treeOrder: Array<{ cat: OrgCategory; level: 1 | 2; orphan?: boolean }>;
  products: OrgProduct[];
  selected: number | null;
  onSelect: (id: number | null) => void;
  canManage: boolean;
  onEdit: (cat: OrgCategory) => void;
  onAdd: () => void;
}> = ({ treeOrder, products, selected, onSelect, canManage, onEdit, onAdd }) => {
  const { t } = useTranslation(['admin', 'orgPages']);
  const counts = useMemo(() => {
    const m = new Map<number, number>();
    products.forEach(p => m.set(p.category_id, (m.get(p.category_id) || 0) + 1));
    return m;
  }, [products]);
  const node = (active: boolean) => `flex min-h-[36px] w-full items-center gap-2 rounded-lg px-2.5 text-left text-sm transition-colors ${
    active ? 'bg-brand-soft font-semibold text-brand' : 'font-medium text-t2 hover:bg-surface-3/60 hover:text-t1'
  }`;
  return (
    <nav aria-label={t('orgPages:catalog.tree.title')} className="flex flex-col gap-0.5" data-testid="org-category-tree">
      <p className="px-2.5 pb-1 text-[11.5px] font-bold uppercase tracking-[0.06em] text-t3">{t('orgPages:catalog.tree.title')}</p>
      <button type="button" className={node(selected === null)} aria-current={selected === null ? 'true' : undefined} onClick={() => onSelect(null)}>
        <span className="min-w-0 flex-1 truncate">{t('orgPages:catalog.tree.all')}</span>
        <span className="text-xs font-medium tabular-nums opacity-70">{products.length}</span>
      </button>
      <div className="my-1 h-px bg-line" />
      {treeOrder.map(({ cat, level }) => {
        const active = selected === cat.id;
        return (
          <div key={cat.id} className={`group relative flex items-center ${level === 2 ? 'pl-5' : ''}`}>
            {level === 2 && <span aria-hidden="true" className="absolute bottom-1 left-2.5 top-1 w-px bg-line-strong/70" />}
            <button type="button" className={`${node(active)} ${canManage ? 'pr-9' : ''}`} aria-current={active ? 'true' : undefined}
              onClick={() => onSelect(cat.id)} title={cat.name}>
              <CatalogToneDot tone={cat.color} />
              <span className={`min-w-0 flex-1 truncate ${cat.is_active ? '' : 'text-t4'}`}>{cat.name}</span>
              <span className="text-xs font-medium tabular-nums opacity-70">{counts.get(cat.id) || 0}</span>
            </button>
            {canManage && (
              <button type="button" onClick={() => onEdit(cat)} aria-label={t('orgPages:catalog.tree.edit', { name: cat.name })}
                title={t('orgPages:catalog.tree.edit', { name: cat.name })}
                className={`absolute right-1 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md transition hover:bg-surface-4/60 hover:text-t1 focus:opacity-100 group-hover:opacity-100 ${
                  active ? 'text-brand opacity-100' : 'text-t4 opacity-0'
                }`}>
                <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            )}
          </div>
        );
      })}
      {canManage && (
        <button type="button" onClick={onAdd}
          className="mt-2 inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-dashed border-line-strong px-2.5 text-sm font-medium text-t2 hover:bg-surface-3/60">
          <Plus className="h-4 w-4" aria-hidden="true" /> {t('orgPages:catalog.tree.add')}
        </button>
      )}
    </nav>
  );
};

/**
 * Δέντρο αριστερά + λίστα δεξιά όταν χωρά (πλάτος της ίδιας της περιοχής ≥ 960, όπως ο «Κατάλογος»)·
 * αλλιώς το δέντρο γίνεται φίλτρο «Κατηγορία» στη γραμμή εργαλείων — στο κινητό μια στήλη 230px
 * θα έτρωγε τη λίστα.
 */
const ProductsLayout: React.FC<{
  tree: React.ReactNode;
  categoryFilter: React.ReactNode;
  children: (filter: React.ReactNode) => React.ReactNode;
}> = ({ tree, categoryFilter, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const layout = useListLayout(ref);
  const withTree = layout === 'wide';
  return (
    <div ref={ref} data-layout={layout} className={withTree ? 'grid grid-cols-[232px_minmax(0,1fr)] gap-6' : ''}>
      {withTree && <aside className="min-w-0 border-r border-line pr-4 pt-[18px]">{tree}</aside>}
      <div className="min-w-0">{children(withTree ? null : categoryFilter)}</div>
    </div>
  );
};

export default OrgCatalogManager;
